/**
 * Примирение сохранённого порядка колонок с фактическим набором.
 *
 * Порядок колонок живёт в `ReactTableController` и персистится в localStorage
 * по `tableId`, а набор колонок приходит из кода и меняется между релизами
 * (добавили колонку, переименовали id, динамический пивот). Пока их никто не
 * сверял, расхождение давало два дефекта сразу: TanStack дописывал незнакомые
 * колонки в конец, а dnd-kit считал перетаскиваемыми только id из сохранённого
 * списка — «чужие» колонки было нельзя перетащить.
 */

/**
 * Примиряет сохранённый порядок с фактическим набором колонок.
 *
 * Правила:
 * - результат — перестановка ровно `actualIds`: ничего лишнего, ничего не потеряно;
 * - id, которых больше нет в наборе, выбрасываются;
 * - относительный порядок сохранённых id сохраняется — перестановка
 *   пользователя не теряется;
 * - новая колонка встаёт на своё место относительно соседей по коду: сразу
 *   после ближайшей известной колонки, которая идёт перед ней в `actualIds`
 *   (а не всегда в конец); несколько новых у одного соседа сохраняют порядок кода;
 *   новая колонка без известных слева встаёт в начало;
 * - пустой или чужой `storedOrder` (ни одного общего id) → `actualIds` как есть.
 *
 * Функция идемпотентна: `reconcile(reconcile(s, a), a)` равно `reconcile(s, a)`,
 * поэтому запись результата обратно в стор не порождает повторных записей.
 *
 * @param storedOrder Порядок из стора таблицы (может быть пустым или устаревшим)
 * @param actualIds Id колонок, которые таблица рисует сейчас (в порядке кода)
 * @returns Порядок для `state.columnOrder` и `SortableContext`
 */
export function reconcileColumnOrder(
  storedOrder: readonly string[],
  actualIds: readonly string[]
): string[] {
  const actual = uniqueIds(actualIds);
  const actualSet = new Set(actual);
  const known = uniqueIds(storedOrder.filter(id => actualSet.has(id)));
  if (known.length === 0) return actual;

  const knownSet = new Set(known);

  // Новые id группируем по «якорю» — ближайшей известной колонке слева по коду.
  // Ключ null — новые колонки, перед которыми известных нет: они пойдут первыми.
  const newByAnchor = new Map<string | null, string[]>();
  let anchor: string | null = null;
  for (const id of actual) {
    if (knownSet.has(id)) {
      anchor = id;
      continue;
    }
    const bucket = newByAnchor.get(anchor);
    if (bucket) bucket.push(id);
    else newByAnchor.set(anchor, [id]);
  }

  const result: string[] = [...(newByAnchor.get(null) ?? [])];
  for (const id of known) {
    result.push(id, ...(newByAnchor.get(id) ?? []));
  }
  return result;
}

/** Поэлементное равенство двух порядков. */
export function isSameOrder(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((id, index) => id === b[index]);
}

/** Непустые строковые id без повторов (первое вхождение выигрывает). */
function uniqueIds(ids: readonly string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const id of ids) {
    if (typeof id !== 'string' || id.length === 0 || seen.has(id)) continue;
    seen.add(id);
    result.push(id);
  }
  return result;
}
